import React from 'react';
import { useSortable } from '@dnd-kit/react/sortable';
import { MsComboBox } from '../../../../components/MsComboBox';
import { ToggleSwitch } from '../../../../components/ToggleSwitch';
import { TagTypeahead } from '../../../../components/TagTypeahead';
import { useDefaultFields } from '../../../../hooks/useLoadFileFormats';
import type { Tag } from '../../../../hooks/useTags';
import type { LoadFileField } from '../../../../types/export';
import { useProjectIdFromRoute } from '../../../../hooks/useProjectIdFromRoute';

export interface FieldRowProps {
  field: LoadFileField;
  index: number;
  saving: boolean;
  onChange: (f: LoadFileField) => void;
  onRemove: () => void;
}

export function FieldRow({ field, index, saving, onChange, onRemove }: FieldRowProps) {
  const projectId = useProjectIdFromRoute();
  const { ref, isDragging } = useSortable({
    id: field.name || `${field.type}-${index}`,
    index,
  });

  const { data: defaultFieldStrings = [] } = useDefaultFields();
  const defaultFieldItems = defaultFieldStrings.map(s => ({ id: s, name: s }));

  const selectedTag: Tag[] = field.parts[0]
    ? [{ id: field.parts[0].id, name: field.parts[0].name, color: '' }]
    : [];

  return (
    <tr ref={ref} style={{ opacity: isDragging ? 0.4 : 1 }}>
      <td style={{ verticalAlign: 'middle', textAlign: 'center', cursor: 'move', width: 24 }}>
        <i className="fas fa-bars fa-fw" style={{ color: '#aaa' }} />
      </td>
      <td>
        {(field.type === 'custom' || field.type === 'tag') ? (
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              value={field.outputName}
              placeholder="Header name"
              disabled={saving}
              onChange={e => onChange({ ...field, outputName: e.target.value })}
            />
            <span className="input-group-btn">
              <button
                type="button"
                className="btn btn-default"
                onClick={onRemove}
                disabled={saving}
                title="Remove field"
                tabIndex={-1}
              >
                <i className="fas fa-trash" />
              </button>
            </span>
          </div>
        ) : (
          <input
            type="text"
            className="form-control"
            value={field.outputName}
            placeholder="Header name"
            disabled={saving}
            onChange={e => onChange({ ...field, outputName: e.target.value })}
          />
        )}
      </td>
      <td style={{ verticalAlign: 'middle' }}>
        {field.type === 'normal' && (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} className="medium">
            {field.name}
          </div>
        )}
        {field.type === 'custom' && (
          <MsComboBox<{ id: string; name: string }>
            selected={field.parts}
            items={defaultFieldItems}
            onChange={parts => onChange({ ...field, parts })}
            onFreeEntry={value => onChange({ ...field, parts: [...field.parts, { id: value, name: value }] })}
            getKey={p => p.id}
            getLabel={p => p.name}
            placeholder="Type or select field parts"
            icon="fa-th"
          />
        )}
        {field.type === 'tag' && (
          <TagTypeahead
            projectId={projectId}
            selected={selectedTag}
            onChange={tags => {
              const t = tags[0] ?? null;
              onChange({ ...field, parts: t ? [{ id: t.id, name: t.name }] : [] });
            }}
            placeholder="Tag name..."
          />
        )}
      </td>
      <td style={{ verticalAlign: 'middle', whiteSpace: 'nowrap', width: 1 }}>
        <ToggleSwitch
          checked={field.isEnabled}
          onChange={isEnabled => onChange({ ...field, isEnabled })}
          onLabel="ON"
          offLabel="OFF"
          size="small"
        />
      </td>
    </tr>
  );
}
